import { useState } from "react"
import { LeafIcon, FlameIcon } from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import { cn } from "@/lib/utils"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { api } from "@/lib/api"
import type { UnitFormState, UnitRow, UnitStatus } from "./useUnitMutations"

interface UnitTypeOption { id: string; code: string; name: string; deletedAt?: string | null }
interface UserOption { id: string; firstName: string; lastName: string; role: string }
interface UnitOption { id: string; code: string; name: string; unitTypeCode?: string; deletedAt?: string | null; unitTypeId?: string }

interface Props {
  open: boolean
  editing: UnitRow | null
  form: UnitFormState
  formError: string | null
  isSaving: boolean
  onFormChange: (patch: Partial<UnitFormState>) => void
  onSubmit: (e: React.FormEvent) => void
  onClose: () => void
}

type TabValue = "general" | "specs"

export function UnitFormSheet({
  open, editing, form, formError, isSaving,
  onFormChange, onSubmit, onClose,
}: Props) {
  const [activeTab, setActiveTab] = useState<TabValue>("general")

  const { data: unitTypeOptions = [] } = useQuery({
    queryKey: ["unit-types-all"],
    queryFn: async () => {
      const res = await api["units"]["unit-types"].$get({ query: {} })
      if (!res.ok) return []
      return res.json() as Promise<UnitTypeOption[]>
    },
    staleTime: 5 * 60 * 1000,
  })

  const { data: userOptions = [] } = useQuery({
    queryKey: ["users-operators"],
    queryFn: async () => {
      const res = await api["users"].$get({ query: {} })
      if (!res.ok) return []
      const all = await res.json() as UserOption[]
      return all.filter(u => u.role === "operator" || u.role === "fleet_manager")
    },
    staleTime: 5 * 60 * 1000,
  })

  const { data: unitOptions = [] } = useQuery({
    queryKey: ["units-refrigeration"],
    queryFn: async () => {
      const res = await api["units"].$get({ query: {} })
      if (!res.ok) return []
      const body = await res.json() as { data: (UnitOption & { unitTypeCode?: string })[], total: number }
      // Filter only refrigeration units (type Q = Equipo de frio)
      return (body.data || []).filter(u => u.unitTypeCode === "Q")
    },
    staleTime: 5 * 60 * 1000,
  })

  const selectedTypeName = unitTypeOptions.find((t) => t.id === form.unitTypeId)
  const selectedOperator = userOptions.find((u) => u.id === form.operatorId)
  const selectedRefrigUnit = unitOptions.find((u) => u.id === form.refrigerationUnitId)

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <SheetContent side="right" className="sm:max-w-sm overflow-y-auto flex flex-col">
        <SheetHeader>
          <SheetTitle>{editing ? "Editar unidad" : "Nueva unidad"}</SheetTitle>
          <SheetDescription>Completá los datos del vehículo o equipo.</SheetDescription>
        </SheetHeader>

        {/* TABS BUTTONS */}
        <div className="flex gap-2 mb-4 -mx-4 px-4 pb-4 border-b">
          <button
            type="button"
            onClick={() => setActiveTab("general")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === "general"
                ? "text-foreground border-b-2 border-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            General
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("specs")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === "specs"
                ? "text-foreground border-b-2 border-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Especificaciones
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">
          {/* ──────────────────────────────────────────────────────────────────────────────── */}
          {/* GENERAL TAB */}
          {/* ──────────────────────────────────────────────────────────────────────────────── */}
          {activeTab === "general" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="uf-code">Código <span className="text-destructive">*</span></Label>
                  <Input id="uf-code" value={form.code}
                    onChange={(e) => onFormChange({ code: e.target.value.toUpperCase() })}
                    maxLength={10} className="uppercase font-mono" placeholder="AA048SI" required />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="uf-unit-number">N° Unidad</Label>
                  <Input id="uf-unit-number" type="number" value={form.unitNumber}
                    onChange={(e) => onFormChange({ unitNumber: e.target.value })}
                    min={1} placeholder="114" />
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="uf-name">Nombre</Label>
                <Input id="uf-name" value={form.name}
                  onChange={(e) => onFormChange({ name: e.target.value })}
                  maxLength={200} placeholder="Movil 42" required />
              </div>

              <div className="grid gap-1.5">
                <Label>Tipo de unidad</Label>
                <Select value={form.unitTypeId} onValueChange={(v) => onFormChange({ unitTypeId: v ?? "" })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccioná un tipo">
                      {selectedTypeName ? `${selectedTypeName.code} — ${selectedTypeName.name}` : null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {unitTypeOptions.filter((t) => !t.deletedAt).map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.code} — {t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="uf-plate">Patente</Label>
                <Input id="uf-plate" value={form.licensePlate}
                  onChange={(e) => onFormChange({ licensePlate: e.target.value.toUpperCase() })}
                  maxLength={20} className="uppercase font-mono" placeholder="AA048SI" />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="uf-vin">VIN / Chasis</Label>
                <Input id="uf-vin" value={form.vin}
                  onChange={(e) => onFormChange({ vin: e.target.value })}
                  maxLength={100} className="font-mono text-xs" placeholder="9BSR4X200G3883160" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="uf-brand">Marca</Label>
                  <Input id="uf-brand" value={form.brand}
                    onChange={(e) => onFormChange({ brand: e.target.value })}
                    maxLength={100} placeholder="SCANIA" />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="uf-model">Modelo</Label>
                  <Input id="uf-model" value={form.model}
                    onChange={(e) => onFormChange({ model: e.target.value })}
                    maxLength={100} placeholder="G410 A4X2" />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="uf-year">Año</Label>
                  <Input id="uf-year" type="number" value={form.manufactureYear}
                    onChange={(e) => onFormChange({ manufactureYear: e.target.value })}
                    min={1900} max={2100} placeholder="2016" />
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="uf-engine">N° Motor</Label>
                <Input id="uf-engine" value={form.engineNumber}
                  onChange={(e) => onFormChange({ engineNumber: e.target.value })}
                  maxLength={100} className="font-mono text-xs" placeholder="8271521" />
              </div>

              <div className="grid gap-1.5">
                <Label>Combustible</Label>
                <div className="flex gap-2">
                  {([
                    { value: null,     label: "—",     active: "bg-muted text-muted-foreground" },
                    { value: "diesel", label: "diesel", active: "bg-orange-100 text-orange-700 border-orange-300" },
                    { value: "gnc",    label: "gnc",    active: "bg-green-100 text-green-700 border-green-300" },
                  ] as const).map((opt) => {
                    const isActive = (form.fuelType || null) === opt.value
                    return (
                      <button
                        key={String(opt.value)}
                        type="button"
                        onClick={() => onFormChange({ fuelType: opt.value ?? "" })}
                        className={cn(
                          "flex-1 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                          isActive
                            ? opt.active + " border-current"
                            : "border-input bg-background text-muted-foreground hover:bg-muted",
                        )}
                      >
                        {opt.value === "diesel" ? (
                          <span className="flex items-center justify-center gap-1.5"><FlameIcon className="size-3.5 text-red-400" /> Diesel</span>
                        ) : opt.value === "gnc" ? (
                          <span className="flex items-center justify-center gap-1.5"><LeafIcon className="size-3.5 text-emerald-500" /> GNC</span>
                        ) : opt.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label>Operador responsable</Label>
                <Select
                  value={form.operatorId || "__none__"}
                  onValueChange={(v) => onFormChange({ operatorId: v === "__none__" ? "" : (v ?? "") })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sin asignar">
                      {selectedOperator ? `${selectedOperator.firstName} ${selectedOperator.lastName}` : "Sin asignar"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin asignar</SelectItem>
                    {userOptions.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.firstName} {u.lastName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-1.5">
                <Label>Estado</Label>
                <Select value={form.status} onValueChange={(v) => onFormChange({ status: v as UnitStatus })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="in_repair">En reparación</SelectItem>
                    <SelectItem value="damaged">Siniestrada</SelectItem>
                    <SelectItem value="missing_docs">Falta documentación</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="uf-location">Ubicación</Label>
                <Input id="uf-location" value={form.location}
                  onChange={(e) => onFormChange({ location: e.target.value })}
                  maxLength={200} placeholder="Ej: Playa Kaizen" />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="uf-observations">Observaciones</Label>
                <Input id="uf-observations" value={form.observations}
                  onChange={(e) => onFormChange({ observations: e.target.value })}
                  placeholder="Notas adicionales" />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="uf-satellite"
                  checked={form.hasSatellite}
                  onCheckedChange={(v) => onFormChange({ hasSatellite: v === true })}
                />
                <Label htmlFor="uf-satellite" className="cursor-pointer">Tiene SATELITAL instalado</Label>
              </div>
            </div>
          )}

          {/* ──────────────────────────────────────────────────────────────────────────────── */}
          {/* SPECS TAB */}
          {/* ──────────────────────────────────────────────────────────────────────────────── */}
          {activeTab === "specs" && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Especificaciones técnicas opcionales del vehículo.</p>

              <div className="grid gap-1.5">
                <Label htmlFor="uf-tare">Peso en vacío (kg)</Label>
                <Input id="uf-tare" type="number" value={form.tare}
                  onChange={(e) => onFormChange({ tare: e.target.value })}
                  min={0} placeholder="18000" />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="uf-axles">Ejes</Label>
                <Input id="uf-axles" value={form.axles}
                  onChange={(e) => onFormChange({ axles: e.target.value })}
                  maxLength={10} placeholder="3" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="uf-length">Largo (m)</Label>
                  <Input id="uf-length" type="number" step="0.01" value={form.lengthM}
                    onChange={(e) => onFormChange({ lengthM: e.target.value })}
                    min={0} placeholder="13.60" />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="uf-width">Ancho (m)</Label>
                  <Input id="uf-width" type="number" step="0.01" value={form.widthM}
                    onChange={(e) => onFormChange({ widthM: e.target.value })}
                    min={0} placeholder="2.48" />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="uf-height">Alto (m)</Label>
                  <Input id="uf-height" type="number" step="0.01" value={form.heightM}
                    onChange={(e) => onFormChange({ heightM: e.target.value })}
                    min={0} placeholder="3.00" />
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="uf-seals">Cantidad de sellos</Label>
                <Input id="uf-seals" type="number" value={form.sealCount}
                  onChange={(e) => onFormChange({ sealCount: e.target.value })}
                  min={0} placeholder="2" />
              </div>

              <div className="grid gap-1.5">
                <Label>Equipo de refrigeración</Label>
                <Select
                  value={form.refrigerationUnitId || "__none__"}
                  onValueChange={(v) => onFormChange({ refrigerationUnitId: v === "__none__" ? "" : (v ?? "") })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sin equipo de refrigeración">
                      {selectedRefrigUnit ? `${selectedRefrigUnit.code} — ${selectedRefrigUnit.name}` : "Sin equipo"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin equipo de refrigeración</SelectItem>
                    {unitOptions.filter((u) => !u.deletedAt).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.code} — {u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="uf-temp-sensors">Cantidad de sensores de temperatura</Label>
                <Input id="uf-temp-sensors" type="number" value={form.tempSensorCount}
                  onChange={(e) => onFormChange({ tempSensorCount: e.target.value })}
                  min={0} placeholder="2" />
              </div>

              <div className="flex flex-col gap-3 pt-1">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="uf-mapped"
                    checked={form.isMapped}
                    onCheckedChange={(v) => onFormChange({ isMapped: v === true })}
                  />
                  <Label htmlFor="uf-mapped" className="cursor-pointer">Está mapeado</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="uf-calibrated"
                    checked={form.isCalibrated}
                    onCheckedChange={(v) => onFormChange({ isCalibrated: v === true })}
                  />
                  <Label htmlFor="uf-calibrated" className="cursor-pointer">Está calibrado</Label>
                </div>
              </div>
            </div>
          )}

          {formError && (
            <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
          )}

          <SheetFooter className="px-0 mt-auto pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear unidad"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
